import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";

// Landing page for the emailed reset link: /reset-password?token=…
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const isWelcome = params.get("welcome") === "1"; // invite flow vs forgot-password
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen qd-admin grid place-items-center p-6">
      <div className="w-full max-w-sm bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
        <div className="w-10 h-10 rounded-lg bg-[var(--qd-charcoal)] grid place-items-center mb-6">
          {done ? <CheckCircle2 className="w-4 h-4 text-white" aria-hidden /> : <KeyRound className="w-4 h-4 text-white" aria-hidden />}
        </div>
        {done ? (
          <>
            <h1 className="font-display text-2xl font-bold tracking-tight">Password updated</h1>
            <p className="text-sm text-gray-500 mt-2" data-testid="reset-done">Sign in with your new password.</p>
            <div className="mt-6 space-y-2">
              <Link to="/admin/login" data-testid="reset-to-admin"><Button className="w-full h-11 rounded-lg bg-[var(--qd-charcoal)] hover:bg-black text-white font-semibold">Restaurant sign in</Button></Link>
              <Link to="/login" className="block text-sm text-gray-500 hover:text-gray-800 text-center pt-2">Customer sign in</Link>
            </div>
          </>
        ) : !token ? (
          <>
            <h1 className="font-display text-2xl font-bold tracking-tight">Invalid link</h1>
            <p className="text-sm text-gray-500 mt-2">This reset link is incomplete. Request a new one.</p>
            <Link to="/forgot-password" className="block mt-4 text-sm font-semibold text-[var(--qd-primary)] text-center">Request reset link</Link>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold tracking-tight">{isWelcome ? "Set your password" : "Choose a new password"}</h1>
            <p className="text-sm text-gray-500 mb-6">{isWelcome ? "Welcome to QRDine — pick the password for your new account. Minimum 6 characters." : "Minimum 6 characters."}</p>
            <form onSubmit={submit} className="space-y-3">
              <PasswordInput data-testid="reset-password" required placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 rounded-lg" />
              <PasswordInput data-testid="reset-password2" required placeholder="Repeat new password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-11 rounded-lg" />
              <Button data-testid="reset-submit" type="submit" disabled={busy} className="w-full h-11 rounded-lg bg-[var(--qd-charcoal)] hover:bg-black text-white font-semibold">
                {busy ? "Saving…" : "Set new password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
